<template>
  <div class="company-renewal">
    <div class="title">
      <h3>{{ $t(`company.menu.label.employeeApplication.items.renewal`) }}</h3>
      <el-divider />
      <el-card v-loading="loading" element-loading-background="#ffffff">
        <div v-if="batches.length > 0" >
          <h3 class="reminder-title"><el-icon><i class="bi bi-list-task"></i></el-icon>{{ i18nNameFormatting("company.renewal.process.title", {year: currentBatchDateInfo.year, quarter: currentBatchDateInfo.quarter}) }}</h3>          
          <el-row :gutter="30">
            <el-col :xs="24" :sm="24" :lg="12">
              <el-card>
                <!-- Company -->
                <template #header>
                  <el-icon style="font-size: 24px;margin-right: 5px;">
                    <i class="bi bi-building-fill"></i>
                  </el-icon>
                  <span style="font-size: 18px;font-weight: bold;vertical-align: text-bottom;">{{ $t('company.renewal.process.label.company') }}</span>
                </template>
                <el-steps direction="vertical" :active="1" class="process-steps">
                  <el-step status="process">
                    <template #title>
                      <h3 class="nomargin" v-html="$t('company.renewal.process.steps.company.cancel.title')" />
                    </template>
                    <template #description>
                      <h3 class="process-step-content" v-html="$t('company.renewal.process.steps.company.cancel.content')" />
                      <br />
                    </template>
                  </el-step>
                  <el-step status="process">
                    <template #title>
                      <h3 class="nomargin" v-html="$t('company.renewal.process.steps.company.confirm.title')" />
                    </template>
                    <template #description>
                      <el-steps
                        simple
                        class="important-dates"
                      >
                        <el-step process-status="process">
                          <template #title>
                            <el-icon>
                              <i :class="$t('company.renewal.process.items.confirmOpen.icon')" />
                            </el-icon>
                            <p>
                              {{ $t("company.renewal.process.items.confirmOpen.label") }}
                            </p>
                            <el-tag
                                type="info"
                                effect="dark"
                                color="#11254a"
                                size="large"
                              >
                                <label class="important-date-label">{{ currentBatchDateInfo.confirmOpen }}</label>
                              </el-tag>
                          </template>
                        </el-step>
                        <el-step process-status="process">
                          <template #title>
                            <el-icon>
                              <i :class="$t('company.renewal.process.items.confirmClose.icon')" />
                            </el-icon>
                            <p>
                              {{ $t("company.renewal.process.items.confirmClose.label") }}
                            </p>
                            <div>
                              <el-tag
                                type="info"
                                effect="dark"
                                color="#11254a"
                                size="large"
                              >
                                <label class="important-date-label">{{ currentBatchDateInfo.confirmClose }}</label>
                              </el-tag>
                            </div>
                          </template>
                        </el-step>
                      </el-steps>
                    </template>
                  </el-step>
                </el-steps>
              </el-card>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-card>
                <!-- Staff -->
                <template #header>
                  <el-icon style="font-size: 24px;margin-right: 5px;">
                    <i class="bi bi-person-fill"></i>
                  </el-icon>
                  <span style="font-size: 18px;font-weight: bold;vertical-align: text-bottom;">{{ $t('company.renewal.process.label.staff') }}</span>
                </template>
                <el-steps direction="vertical" :active="1" class="process-steps">
                  <el-step status="process">
                    <template #title>
                      <h3 class="nomargin" v-html="$t('company.renewal.process.steps.staff.submit.title')" />
                    </template>
                    <template #description>                      
                      <el-steps
                        simple
                        class="important-dates"
                      >
                        <el-step process-status="process">
                          <template #title>
                            <el-icon>
                              <i :class="$t('company.renewal.process.items.appStart.icon')" />
                            </el-icon>
                            <p>
                              {{ $t("company.renewal.process.items.appStart.label") }}
                            </p>
                            <el-tag
                                type="info"
                                effect="dark"
                                color="#11254a"
                                size="large"
                              >
                                <label class="important-date-label">{{ currentBatchDateInfo.appStart }}</label>
                              </el-tag>
                          </template>
                        </el-step>
                        <el-step process-status="process">
                          <template #title>
                            <el-icon>
                              <i :class="$t('company.renewal.process.items.appClose.icon')" />
                            </el-icon>
                            <p>
                              {{ $t("company.renewal.process.items.appClose.label") }}
                            </p>
                            <el-tag
                                type="info"
                                effect="dark"
                                color="#11254a"
                                size="large"
                              >
                                <label class="important-date-label">{{ currentBatchDateInfo.appClose }}*</label>
                              </el-tag>
                          </template>
                        </el-step>
                      </el-steps>
                      <h3 class="process-step-content" v-html="i18nNameFormatting('company.renewal.process.steps.staff.submit.content', {expiry: currentBatchDateInfo.expiry})" />
                      <br />
                    </template>
                  </el-step>
                  <el-step status="process">
                    <template #title>
                      <h3 class="nomargin" v-html="$t('company.renewal.process.steps.staff.approve.title')" />
                    </template>
                  </el-step>
                </el-steps>
              </el-card>
            </el-col>
          </el-row>
          
          <br/>
          <el-collapse v-model="selectedBatch.id" accordion class="batch-item" @change="handleCollapseChange">
            <el-collapse-item v-for="batch in batches" v-bind:key="batch.id" :name="batch.id" v-loading="selectedBatch.loading && batch.id == selectedBatch.id">
              <template #title>
                <el-icon class="status-icon">
                  <i :class="getIconClass(batch.status)" />
                </el-icon>
                <span class="batch-header">
                  {{ getBatchHeader(batch.batchDateEnd) }}
                </span>
              </template>
              <div v-if="selectedBatch.fetchSuccess == false" class="load-fail text-center">
                <h3>
                  {{ $t("exceptions.failGetData") }}
                </h3>
                <p class="retry-block" @click="handleCollapseChange(selectedBatch.id)">
                  <el-icon>
                    <i class="bi bi-arrow-clockwise" />
                  </el-icon>
                  <br/>
                  <span>
                    {{ $t("common.button.retry") }}
                  </span>
                </p>
              </div>
              <div v-if="selectedBatch.fetchSuccess == true">
                <el-alert
                  class="custom-block warning"
                  type="warning"
                  :closable="false"
                >
                  <p v-html="displayReminder(batch.companyCode)" />
                </el-alert>
                <el-card>
                  <div class="confirm-div">
                    <el-row v-if="batch.status == 1 || batch.status == 4">
                      <el-col :span="20">
                        <el-icon class="status-icon">
                          <i class="bi bi-info-circle-fill info" />
                        </el-icon>
                        <span class="renew-note">{{ $t("company.renewal.label.batchRenew.note") }}</span>
                      </el-col>
                      <el-col :span="4" class="confirm-btn-col">
                        <el-button type="primary" @click="handleConfirm">
                          {{ $t("common.button.confirm") }}
                        </el-button>
                      </el-col>
                    </el-row>
                    <div v-else>
                      <!-- <p v-if="batch.status == 4" style="text-align: right;">
                        <el-tag size="large" effect="light" type="warning" :hit="true">{{ $t("company.renewal.label.batchRenew.confirmStatus.N") }}</el-tag>
                      </p> -->
                      <el-row :gutter="20">
                        
                        <el-col :span="9">
                          {{ $t("company.renewal.label.batchRenew.confirmBy") }}<br/>{{ batch.confirmBy }}
                        </el-col>
                        <el-col :span="9">
                          {{ $t("company.renewal.label.batchRenew.confirmTime") }}<br/>{{ formatDT(batch.confirmTime) }}
                        </el-col>
                        <el-col :span="6" style="text-align: right;">
                          <el-tag size="large" effect="light" type="success" :hit="true">{{ $t("company.renewal.label.batchRenew.confirmStatus.Y") }}</el-tag>
                        </el-col>
                      </el-row>
                    </div>
                  </div>
                  <el-divider />
                  <el-row :gutter="20" class="batch-renew-lists">
                    <el-col :xs="24" :lg="12" v-loading="selectedBatch.renewList.loading">
                      <h3 class="text-center">{{ $t("company.renewal.label.batchRenew.renewList.title") }} ({{ selectedBatch.renewList.page.count }})<br/>&nbsp;</h3>
                      <div v-if="selectedBatch.renewList.fetchSuccess == false" class="load-fail text-center">
                        <h3>
                          {{ $t("exceptions.failGetData") }}
                        </h3>
                        <p class="retry-block" @click="loadRenewStaffList(selectedBatch.id)">
                          <el-icon>
                            <i class="bi bi-arrow-clockwise" />
                          </el-icon>
                          <br/>
                          <span>
                            {{ $t("common.button.retry") }}
                          </span>
                        </p>
                      </div>
                      <div class="table" v-if="selectedBatch.renewList.fetchSuccess == true">
                        <el-table :data="selectedBatch.renewList.list" 
                          :empty-text="this.$i18n.t('company.enquiry.noData')
                          " style="width: 100%">
                          <el-table-column :label="th('name')" prop="staffName" min-width="50%"></el-table-column>
                          <el-table-column :label="th('email')" prop="email" min-width="50%"></el-table-column>
                        </el-table>
                        <div class="pagination" v-if="selectedBatch.renewList.page.count > 0">
                          <el-config-provider :locale="getLang">
                            <el-pagination class="pagination" :currentPage="selectedBatch.renewList.page.currentPage" :page-size="selectedBatch.renewList.page.pagesize" :page-sizes="[25, 50, 100]"
                              layout="sizes, prev, pager, next" :total="selectedBatch.renewList.page.count" @size-change="handleSizeChange($event, 'renewList')"
                              @current-change="handlePageChange($event, 'renewList')" />
                          </el-config-provider>
                        </div>
                      </div>
                    </el-col>
                    <el-divider direction="horizontal" class="hidden-lg-and-up" />
                    <el-divider direction="vertical" class="hidden-md-and-down" />
                    <el-col :xs="24" :lg="12" v-loading="selectedBatch.resignList.loading">
                      <h3 class="text-center">{{ $t("company.renewal.label.batchRenew.resignList.title") }} ({{ selectedBatch.resignList.page.count }})<br/>{{ getBatchPeriod(batch.batchDateStart, batch.batchDateEnd) }}</h3>
                      <div v-if="selectedBatch.resignList.fetchSuccess == false" class="load-fail text-center">
                        <h3>
                          {{ $t("exceptions.failGetData") }}
                        </h3>
                        <p class="retry-block" @click="loadResignStaffList(selectedBatch.id)">
                          <el-icon>
                            <i class="bi bi-arrow-clockwise" />
                          </el-icon>
                          <br/>
                          <span>
                            {{ $t("common.button.retry") }}
                          </span>
                        </p>
                      </div>
                      <div class="table" v-if="selectedBatch.resignList.fetchSuccess == true">
                        <el-table :data="selectedBatch.resignList.list" v-loading="selectedBatch.resignList.loading"
                          :empty-text="this.$i18n.t('company.enquiry.noData')" style="width: 100%;">
                          <el-table-column :label="th('name')" prop="staffName" min-width="50%"></el-table-column>
                          <el-table-column :label="th('searchIdentityNum')" prop="identityNumber" min-width="50%"></el-table-column>
                        </el-table>
                        <div class="pagination" v-if="selectedBatch.resignList.page.count > 0">
                          <el-config-provider :locale="getLang">
                            <el-pagination class="pagination" :currentPage="selectedBatch.resignList.page.currentPage" :page-size="selectedBatch.resignList.page.pagesize" :page-sizes="[25, 50, 100]"
                              layout="sizes, prev, pager, next" :total="selectedBatch.resignList.page.count" @size-change="handleSizeChange($event, 'resignList')"
                              @current-change="handlePageChange($event, 'resignList')" />
                          </el-config-provider>
                        </div>
                      </div>
                    </el-col>
                  </el-row>
                  <el-divider />
                  <!-- <div class="confirm-div">
                    <el-row v-if="batch.status == 1">
                      <el-col :span="16">
                        <h3>
                          {{ $t("company.renewal.label.batchRenew.confirm") }}
                        </h3>
                      </el-col>
                      <el-col :span="8" class="confirm-btn-col">
                        <el-button type="primary" @click="handleConfirm">
                          {{ $t("common.button.confirm") }}
                        </el-button>
                      </el-col>
                    </el-row>
                    <div v-else>
                      <p v-if="batch.status == 4" style="text-align: right;">
                        <el-tag size="large" effect="light" type="warning" :hit="true">{{ $t("company.renewal.label.batchRenew.confirmStatus.N") }}</el-tag>
                      </p>
                      <el-row :gutter="20" v-else>
                        
                        <el-col :span="9">
                          {{ $t("company.renewal.label.batchRenew.confirmBy") }}<br/>{{ batch.confirmBy }}
                        </el-col>
                        <el-col :span="9">
                          {{ $t("company.renewal.label.batchRenew.confirmTime") }}<br/>{{ formatDT(batch.confirmTime) }}
                        </el-col>
                        <el-col :span="6" style="text-align: right;">
                          <el-tag size="large" effect="light" type="success" :hit="true">{{ $t("company.renewal.label.batchRenew.confirmStatus.Y") }}</el-tag>
                        </el-col>
                      </el-row>
                    </div>
                  </div> -->
                </el-card>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
        <div v-else>
          <el-divider />
          <p class="text-center">{{ $t("company.table.body.noRecord") }}</p>
          <el-divider />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import "@/style/common.css";
import renewalAPI from '@/http/apis/company/renewal';
import TableView from './TableView.vue';
import { authenticateJWT, i18nNameFormatting, handleCompanyException } from '@/libs/common';
import moment from 'moment';
import eng from "element-plus/es/locale/lang/en";
import chi from "element-plus/es/locale/lang/zh-tw"
import { WarningAlertWithCallback, ConfirmDialog } from '@/plugins/message';
import * as loading from "@/plugins/loading";
import { ConfirmDialogWithSingleBtn } from '../../plugins/message';
import { useCompanyStore } from "@/store/company";

export default {
  name: "CompanyRenewal",
  extends: TableView,
  setup() {
    return {
      companyStore: useCompanyStore()
    }
  },
  data() {
    return {
      batches: [],
      selectedBatch: {
        loading: false,
        fetchSuccess: null,
        id: -1,
        renewList: {
          loading: false,
          fetchSuccess: false,
          list: [],
          page: {
            count: 0,
            pagesize: 25,
            currentPage: 1,
          }
        },
        resignList: {
          loading: false,
          fetchSuccess: false,
          list: [],
          page: {
            count: 0,
            pagesize: 25,
            currentPage: 1,
          }
        }
      },
      loading: true
    }
  },
  computed: {
    getLang() {
      return this.$i18n.locale == "ch" ? chi : eng;
    },
    currentBatchDateInfo() {
      let result = {
        year: null,
        quarter: null,
        confirmOpen: null,
        confirmClose: null,
        appStart: null,
        appClose: null,
        expiry: null
      }

      if (this.batches.length > 0) {
        let startDate = moment(this.batches[0].batchDateStart);
        let endDate = moment(this.batches[0].batchDateEnd);
        result.year = endDate.year();
        result.quarter = endDate.quarter();
        result.confirmOpen = startDate.clone().add(1, "months").format("D/M/YYYY");
        result.confirmClose = endDate.format("D/M/YYYY");
        result.appStart = endDate.clone().add(1, 'days').startOf('day').format("D/M/YYYY");
        result.appClose = endDate.clone().add(15, 'days').startOf('day').format("D/M/YYYY");
        result.expiry = endDate.clone().add(1, 'months').endOf('month').format("D/M/YYYY");
      }
      return result;
    }
  },
  methods: {
    formatDT(dateTimeStr) {
      return moment(dateTimeStr).format("YYYY/MM/DD HH:mm:ss")
    },
    getIconClass(status) {
      let className = null;
      switch (status) {
        case 1:
          className = "bi bi-opencollective"
          break;
        case 4:
          className = "bi bi-x-circle-fill warning"
          break;
        default:
          className = "bi bi-check-circle-fill success"
          break;
      }

      return className;
    },
    getBatchHeader(batchEnd) {
      let endDTObj = moment(batchEnd);
      return i18nNameFormatting("company.renewal.label.batchRenew.header", {
        year: endDTObj.year(),
        quarter: endDTObj.quarter()
      })
    },
    getBatchPeriod(batchStart, batchEnd) {
      let startDTObj = moment(batchStart);
      let endDTObj = moment(batchEnd);
      return i18nNameFormatting("company.renewal.label.batchRenew.period", {
        startY: startDTObj.year(),
        endY: endDTObj.year(),
        startM: startDTObj.month() + 1,
        endM: endDTObj.month() + 1
      })
    },
    handleSizeChange(val, target) {
      this.selectedBatch[target].page.pagesize = val;
      this.selectedBatch[target].page.currentPage = 1;
      switch(target) {
        case "renewList":
          this.loadRenewStaffList(this.selectedBatch.id)
          break;
        case "resignList":
          this.loadResignStaffList(this.selectedBatch.id)
          break;
      }
    },
    handlePageChange(currentPage, target) {
      this.selectedBatch[target].page.currentPage = currentPage;
      switch(target) {
        case "renewList":
          this.loadRenewStaffList(this.selectedBatch.id)
          break;
        case "resignList":
          this.loadResignStaffList(this.selectedBatch.id)
          break;
      }
    },
    async handleCollapseChange(val) {
      if(val) {
        this.selectedBatch.loading = true;
        try {
          await this.loadData(val);
        } catch(err) {
          console.log(err)
        } finally {
          this.selectedBatch.loading = false;
        }
      }
    },
    handleConfirm() {
      let dialogMsgObj = this.$i18n.tm("company.renewal.label.batchRenew.confirmDialog");
      ConfirmDialog(dialogMsgObj.title, dialogMsgObj.message, () => {
        let loadingInstance = loading.create("process");
        let jwt = this.companyStore.jwt;
        let formData = new FormData();
        formData.append("Id", this.selectedBatch.id);
        renewalAPI.postConfirm(jwt, formData).then(() => {
          ConfirmDialogWithSingleBtn("", this.$i18n.t("company.message.success.confirm"), () => {
            this.loadBatches();
          }, () => {}, {
            type: "success"
          })
        }).catch((err) => {
          console.log(err)
          handleCompanyException(err)
        }).finally(() => {
          loadingInstance.close();
        });
      }, () => {}, () => {});
    },
    async loadData(id) {
      this.selectedBatch.fetchSuccess = null;
      let resArr = [];
      resArr.push(await this.loadRenewStaffList(id));
      resArr.push(await this.loadResignStaffList(id));
      console.log(resArr);
      this.selectedBatch.fetchSuccess = (resArr.length == 2 && resArr.every(bool => bool == true));
    },
    async loadRenewStaffList(id) {
      let batchObj = this.batches.find(obj => obj.id == id);
      let jwt = this.companyStore.jwt;
      let renewList = this.selectedBatch.renewList;
      let loadRes = true;
      try {
        this.selectedBatch.renewList.loading = true;
        this.selectedBatch.renewList.fetchSuccess = null;

        let renewCountRes = await renewalAPI.getRenewStaffCount(jwt, batchObj.batchDateEnd);
        // await renewalAPI.getRenewStaffCount(jwt, batchObj.batchDateEnd);
        // throw new Error("xx")
        if (renewCountRes.data >= 0) {
          renewList.page.count = renewCountRes.data;
        }
        let renewListRes = await renewalAPI.getRenewStaffList(jwt, batchObj.batchDateEnd, (renewList.page.currentPage - 1) * renewList.page.pagesize, renewList.page.pagesize);
        if (renewListRes.data) {
          renewList.list = renewListRes.data;
        }
        this.selectedBatch.renewList.loading = false;
      } catch(err) {
        console.log(err)
        loadRes = false
      }

      this.selectedBatch.renewList.fetchSuccess = loadRes;

      return loadRes;
    },
    async loadResignStaffList(id) {
      let batchObj = this.batches.find(obj => obj.id == id);
      let jwt = this.companyStore.jwt;
      let resignList = this.selectedBatch.resignList;
      let loadRes = true;
      try {
        this.selectedBatch.resignList.loading = true;
        this.selectedBatch.resignList.fetchSuccess = null;

        let resignListRes = await renewalAPI.getResignedList(jwt, batchObj.batchDateStart, batchObj.batchDateEnd, resignList.page.currentPage -1, resignList.page.pagesize);
        if (resignListRes.data) {
          resignList.list = resignListRes.data.list;
          resignList.page.count = resignListRes.data.totalRecords;
        }
        this.selectedBatch.resignList.loading = false;
      } catch(err) {
        console.log(err);
        loadRes = false;
      }

      this.selectedBatch.resignList.fetchSuccess = loadRes;

      return loadRes;
    },
    async loadBatches() {
      this.loading = true;
      let storedJWT = this.companyStore.jwt;

      let res = await renewalAPI.getRenewBatchList(storedJWT);
      if (res.status == 200) {
        this.batches = res.data;
        this.loading = false;
      } else {
        WarningAlertWithCallback(this.$i18n.t("exceptions.failGetData"), "", {
          showClose: false,
          callback: ()=> {window.location.reload()}
        })
      }
    },
    displayReminder(companyCode) {
      return i18nNameFormatting("company.renewal.label.batchRenew.reminder", {
        link: `${window.location.origin}/${this.$i18n.locale}/email/${companyCode}` 
      });
    },
    i18nNameFormatting(path, replaceObj) {
      return i18nNameFormatting(path, replaceObj)
    }
  },
  beforeRouteEnter (to, from, next) {
    const companyStore = useCompanyStore();
    next(async vm => {
      let storedJWT = companyStore.jwt;
      if(!authenticateJWT(storedJWT)) {
        // handleCompanySessionExpired();
        return;
      }
      
      await vm.loadBatches()
    });
  }
}
</script>

<style scoped>
.custom-block.warning {
  margin-bottom: 20px;
  border-left: 8px solid #e6a23c;
}
.toolbar,
.title {
  margin: 10px 20px;
}

.toolbar .el-button {
  min-width: 100px;
}

.toolbar .right {
  text-align: right;
}

.table-footer {
  background-color: white;
  margin: 0;
  padding: 10px 20px;
  text-align: right;
}

.table {
  margin: 0px 20px 20px 20px;
}

.company-renewal>>>.el-table {
  width: 100%;
}

.company-renewal>>>.el-table__append-wrapper {
  text-align: right;
  padding: 10px;
}

/* .company-renewal>>>.el-form-item__label {
  width: 80px;
} */

.company-renewal>>>.el-upload,
.company-renewal>>>.el-upload .el-upload-dragger {
  width: 100%;
}

.company-renewal>>>.el-step__head.is-wait,
.company-renewal>>>.el-step__title.is-wait,
.company-renewal>>>.el-step__description.is-wait {
  color: var(--el-text-color-primary);
  border-color: var(--el-text-color-primary);
  font-size: 14px;
}

.company-renewal>>>.el-step__title.is-wait {
  font-size: 16px;
  font-weight: bold;
}

.company-renewal>>>.error-row {
  --el-table-tr-bg-color: var(--el-color-danger-light-8);
}

.company-renewal>>>.validation-error {
  color: red;
  font-size: 20px;
  line-height: 2;
}

.company-renewal>>>.add-staff-form .el-form-item {
  display: block;
  margin: 0;
}

.company-renewal>>>.staff-dialog .el-dialog__body {
  padding: 10px 20px;
}

.company-renewal>>>.staff-dialog-header {
  margin: 5px 0px 0px 0px;
  font-weight: bold;
}

.company-renewal:deep(.el-collapse-item__title) {
  display: flex;
  justify-content: start;
  align-items: center;
}

.status-icon {
  font-size: 30px;
  vertical-align: middle;
}
.status-icon:has(> .info) {
  color: #409eff
}
.status-icon:has(> .success) {
  color: #67c23a
}
.status-icon:has(> .warning) {
  color: #e6a23c
}
.batch-header {
  margin-left: 20px;
  font-size: 18px;
  font-weight: bolder;
  
}
.batch-item:deep(.el-collapse-item__header.is-active) {
  color: #11254a !important;
  background-color: transparent !important;
}
.batch-item:deep(.el-collapse-item__header) {
  border: 2px solid #ddd;
  background-color: transparent !important;
  padding: 0px !important
}
.batch-item:deep(.el-collapse-item__header>.el-collapse-item__title) {
  padding-left: 15px;
  max-width: calc(100% - 45px);
}

.batch-item:deep(.el-collapse-item) {
  margin-top: 20px;
  margin-bottom: 20px;
}
.batch-renew-lists:deep(.el-divider--vertical) {
  height: auto;

}
@media screen and (min-width: 1200px) {
  .batch-renew-lists {
    flex-wrap: nowrap;
  }
}
.batch-renew-lists {
  justify-content: center;
}
.confirm-btn-col {
  display: flex;
  align-items: center;
  justify-content: end;
}
/* .important-dates:deep(.el-step.is-simple) {
  flex-direction: column;
} */
.important-dates {
  padding: 10px 25px;
  width: 75%;
}
.important-dates:deep(.el-step__head) {
  display: none;
}
.important-dates:deep(.el-icon) {
  font-size: 35px;
}
.important-dates:deep(.el-step.is-simple) {
  flex: 1;
}
.important-dates:deep(.el-step.is-simple .el-step__main) {
  justify-content: center;
}
.important-dates:deep(.el-step.is-simple:first-child) {
  max-width: 66% !important;
}
.important-dates:deep(.el-step.is-simple:first-child .el-step__title) {
  min-width: 50% !important;
}
.important-dates:deep(.el-step.is-simple:first-child .el-step__arrow) {
  max-width: 50% !important;
}
.important-dates:deep(.el-step.is-simple:last-child) {
  min-width: 33% !important;
}
.important-dates:deep(.el-step.is-simple:last-child) {
  min-width: 33% !important;
}
.important-dates:deep(.el-step__title) {
  text-align: center;
}
.custom-block:deep(.el-alert__content) {
  width: 100%;
}
.custom-block.warning:deep(.el-link--inner) {
  font-size: 16px;
}
.important-date-label {
  font-size: 16px;
  padding: 10px;
  font-weight: bold;
}
.load-fail {
  font-size: 16px;
  font-weight: bold;
}
.retry-block {
  cursor: pointer;
}
.retry-block .el-icon{
  font-size: 50px;
}
.reminder-title .el-icon {
  font-size: 30px;
  margin-right: 10px;
  vertical-align: middle;
  /* color: #409eff; */
}
.reminder-title {
  line-height: 30px;
}
.process-steps:deep(.el-link) {
  vertical-align: baseline;
}
.process-steps:deep(.el-link--inner) {
  font-size: 18px;
}
.process-step-content {
  font-size: 16px;
  /* font-weight: normal; */
}
.renew-note {
  width: calc(100% - 50px);
  display: inline-block;
  vertical-align: middle;
  font-size: 16px;
  font-weight: bold;
  padding: 10px;
}
.confirm-div {
  margin-top: 10px;
}
</style>